// ============================================================
// PINNING - one Willpower test, two callers.
//
// The Pinning weapon special has owned this test since v8.198.4.
// Suppressing Fire needs exactly the same test against everyone in the
// kill zone, at Hard (-20), and a second copy would drift the moment
// either the immunity rule or the test difficulty changed - which is
// the standing lesson in dev-docs/gotchas.md: a new consumer ROUTES
// THROUGH the existing derivation.
//
// So the test lives here and the weapon special delegates. Pure; the
// d100 comes from engine/dice via rollPlainTargetTest, so the seeded
// E2E order is unchanged - ONE roll per subject, exactly as before.
// ============================================================
import type { TestResult } from './characteristicTests';
import { resistTest } from './resistances';
import type { Target } from '../types';

/** Suppressing Fire's pinning test is Hard. The Pinning weapon special tests at no modifier. */
export const SUPPRESSION_PIN_MOD = -20;

export interface PinningOutcome {
  /** Absent when the subject was immune - no die was rolled. */
  test?: TestResult;
  /** True when the subject is now Pinned. */
  pinned: boolean;
  /** True when nothing was rolled because they cannot be pinned at all. */
  immune: boolean;
  /** One line, ready for a narrative note. */
  summary: string;
}

/**
 * Roll one subject's Willpower test against being pinned.
 *
 * `mod` is the difficulty: 0 for the Pinning weapon special, -20 for
 * Suppressing Fire's Hard test. `ignoreImmunity` is Purgatus, which
 * pins things that otherwise cannot be pinned.
 *
 * Immunity short-circuits BEFORE the roll, which is a contract the
 * seeded dice depend on: an immune subject consumes no die.
 */
export function rollPinningTest(target: Target, opts: { mod?: number; ignoreImmunity?: boolean } = {}): PinningOutcome {
  // v8.347.0: vector `fear`, through engine/resistances. The immune set
  // is exactly what the condition map gave 'Pn' - From Beyond, Machine,
  // Stuff of Nightmares, Frenzied - plus Fearless now, and Nerves of
  // Steel re-rolls, as the talent always said it would.
  const out = resistTest(target as unknown as Record<string, unknown>, 'fear', 'willpower', 0, opts.mod ?? 0, {
    ignoreImmunity: opts.ignoreImmunity,
  });
  if (out.immune) {
    return { pinned: false, immune: true, summary: 'cannot be pinned - immune.' };
  }
  const test = out.test!;
  const reroll = out.rerolled
    ? `FAIL WP test (${out.firstTest!.roll} vs ${out.firstTest!.target}) - ${out.resistance.rerollSources.join('/')} re-roll: ${test.roll} - `
    : '';
  const bonus = out.resistance.bonus
    ? `, ${out.resistance.bonus > 0 ? '+' : ''}${out.resistance.bonus} ${out.resistance.bonusSources.join('/')}`
    : '';
  return {
    test,
    pinned: !out.passed,
    immune: false,
    summary: out.passed
      ? `${reroll}Pass WP test (${test.roll} vs ${test.target}${bonus}) - holds steady.`
      : `${reroll}FAIL WP test (${test.roll} vs ${test.target}${bonus}) - PINNED! (Snap Fire only, crawl, WP test to shake off)`,
  };
}
